import React from "react";
import InputFactory from "../InputFactory";
import FormTab from "nq-component/dist/FormFactory/FormTab";
import checkCondition from "nq-component/dist/checkCondition";
import toDisplayCase from "nq-component/dist/toDisplayCase";
import { passwordInputProps } from "../InputFactory/passwordPolicy";

/**
 * Group the fields in the schema based on sections and return the grouped fields object.
 * @param {Object} schema - The schema object containing the fields and sections.
 * @returns {Object} - The grouped fields object.
 */
function group(schema) {
  const { fields, sections } = schema;
  const groups = {
    General: {},
  };
  for (const key in fields) {
    const options = fields[key];
    if (sections && sections[options.section]) {
      groups[options.section] = groups[options.section] || {};
      groups[options.section][key] = options;
    } else {
      groups["General"][key] = options;
    }
  }
  return groups;
}

const defaultProps = {
  excludeFields: [],
  hasLabel: true,
  showSections: true,
  showTabs: true,
};

/**
 * FormFactory component renders a form based on the provided schema and object data.
 * @param {Object} props - The component props.
 * @param {Object} props.schema - The schema object defining the form structure.
 * @param {Object} props.object - The object data to populate the form fields.
 * @param {Function} props.onChange - The callback function called when a form field value changes.
 * @returns {JSX.Element} - The rendered form component.
 */
function FormFactory({
  className,
  schema,
  object,
  onChange,
  excludeFields,
  hasLabel,
  showSections,
  showTabs,
  roles,
  onTabChange,
  disabled,
  disabledFields,
  ...props
}) {
  const tabs = schema.tabs;
  const initialTab =
    showTabs && tabs && Object.keys(tabs).length > 0
      ? Object.keys(tabs)[0]
      : null;
  const [_tab, setTab] = React.useState(initialTab);

  React.useEffect(() => {
    if (showTabs && initialTab) {
      onTabChange?.(initialTab);
    }
  }, [showTabs, initialTab, onTabChange]);

  const handleSetTab = React.useCallback(
    (tab) => {
      setTab(tab);
      onTabChange?.(tab);
    },
    [onTabChange]
  );

  const sections = schema.sections || {};
  const groups = group(schema);
  return (
    <>
      {showTabs && (
        <FormTab
          roles={roles}
          onSet={handleSetTab}
          tabs={tabs}
          object={object}
        />
      )}
      {Object.keys(groups).map((key) => {
        const fields = groups[key];
        const { label } = sections[key] || {};
        const components = Object.keys(fields)
          .map((field) => {
            if (excludeFields.includes(field)) return null;
            const conditions =
              fields[field].if || fields[field].or || fields[field].and;
            if (conditions && !checkCondition(fields[field], object)) {
              return null;
            }
            let { type, pattern, write, tab, col, ...options } = fields[field];
            if (showTabs && _tab && tab && _tab !== tab) return null;
            if (write === false) return null;
            const passwordType = String(options._type || type || "").toLowerCase();
            if (
              field === "password" ||
              field === "confirmPassword" ||
              passwordType === "password"
            ) {
              type = "Password";
              const policy = passwordInputProps({
                pattern,
                minLength: options.minLength,
                title: options.title,
                placeholder: options.placeholder,
              });
              pattern = policy.pattern;
              options = { ...options, ...policy };
            }
            return (
              <div
                className={
                  options._type === "StringArray" ? null : col || className
                }
                key={field}
              >
                {type !== "Boolean" && hasLabel && (
                  <label className="form-label fs-sm">
                    {options.label === "no-label"
                      ? ""
                      : options.label || toDisplayCase(field)}{" "}
                    {(options.required || options._required) && (
                      <span className="text-danger">*</span>
                    )}
                  </label>
                )}
                {props.componentFactory ? (
                  React.createElement(props.componentFactory, {
                    object: object,
                    field: field,
                    onChange: onChange,
                    type: type,
                    ...options,
                    ...props,
                    ...(pattern ? { pattern } : {}),
                    disabled: disabled || disabledFields?.includes(field),
                  })
                ) : (
                  <InputFactory
                    object={object}
                    field={field}
                    onChange={onChange}
                    type={type}
                    {...options}
                    {...props}
                    {...(pattern ? { pattern } : {})}
                    disabled={disabled || disabledFields?.includes(field)}
                  />
                )}
              </div>
            );
          })
          .filter((c) => c);
        return (
          <>
            {components.length > 0 &&
              showSections &&
              Object.keys(sections).length > 0 && (
              <div className="col-12">
                <p className="small fw-bold mb-0 ms-1">{label || key}</p>
                <hr />
              </div>
            )}
            {components}
          </>
        );
      })}
    </>
  );
}

FormFactory.defaultProps = defaultProps;
export default FormFactory;
